import { useEffect, useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

interface TrackState {
  level: number;
  current_xp: number;
  total_xp: number;
  prestige: number;
}

interface ProgState {
  tracks: Record<string, TrackState>;
  skill_ranks: Record<string, Record<string, number>>;
  skill_points: number;
  stat_points: number;
}

export default function Progression({ session }: Props) {
  const playerId = session.playerId;
  const [state, setState] = useState<ProgState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trackId, setTrackId] = useState('character_level');
  const [xpAmount, setXpAmount] = useState(500);
  const [treeId, setTreeId] = useState('warrior');
  const [nodeId, setNodeId] = useState('heavy_strike');
  const [busy, setBusy] = useState(false);

  const base = `/api/v1/games/${session.gameId}/modules/progression/tracks/player/${playerId}`;

  const reload = () => {
    fetch(base)
      .then(r => r.json())
      .then(d => setState(d.state))
      .catch(e => setError(e.message));
  };
  useEffect(reload, [session.gameId, playerId]);

  const grantXp = async () => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/grant-xp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ track_id: trackId, amount: xpAmount }),
      });
      if (!res.ok) throw new Error(await res.text());
      reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const unlockSkill = async () => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/unlock-skill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tree_id: treeId, node_id: nodeId }),
      });
      if (!res.ok) throw new Error(await res.text());
      reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const tracks = state ? Object.entries(state.tracks) : [];
  const trees = state ? Object.entries(state.skill_ranks) : [];
  const short = `${playerId.slice(0, 8)}…${playerId.slice(-4)}`;

  return (
    <Container>
      <PageHeader
        title="Progression"
        subtitle="XP tracks, skill trees, and prestige."
      />

      {error && <div className="error-banner">{error}</div>}

      <div className="card mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-fg-dim mb-1">Player</div>
          <code className="text-fg" title={playerId}>{short}</code>
        </div>
        <div className="flex gap-6 text-right">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-fg-dim mb-1">Skill pts</div>
            <div className="text-2xl font-semibold tabular-nums">{state?.skill_points ?? 0}</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-fg-dim mb-1">Stat pts</div>
            <div className="text-2xl font-semibold tabular-nums">{state?.stat_points ?? 0}</div>
          </div>
        </div>
      </div>

      {/* Tracks */}
      <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-fg-dim mb-3">Tracks</h2>
      {tracks.length === 0 ? (
        <div className="empty mb-5">
          <div className="text-fg-2 font-medium mb-1">No track data</div>
          <div className="text-xs text-fg-muted">Grant XP below after publishing progression tracks.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 mb-5">
          {tracks.map(([id, t]) => (
            <div key={id} className="card hover:border-line-strong transition-colors">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-fg-dim">{id}</div>
                {t.prestige > 0 && <span className="badge badge-current">P{t.prestige}</span>}
              </div>
              <div className="text-[28px] font-semibold tabular-nums tracking-tight">Lv {t.level}</div>
              <div className="mt-2 w-full h-2 bg-bg-3 rounded-full overflow-hidden">
                <div className="h-full bg-brand rounded-full transition-all" style={{ width: `${Math.min(100, (t.current_xp / Math.max(1, t.current_xp + 100)) * 100)}%` }} />
              </div>
              <div className="text-xs text-fg-muted mt-1">{t.current_xp.toLocaleString()} XP · {t.total_xp.toLocaleString()} total</div>
            </div>
          ))}
        </div>
      )}

      {/* Skill ranks */}
      {trees.length > 0 && (
        <>
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-fg-dim mb-3">Skill ranks</h2>
          <div className="table-wrap mb-5">
            <table>
              <thead><tr><th>Tree</th><th>Node</th><th>Rank</th></tr></thead>
              <tbody>
                {trees.flatMap(([tree, nodes]) =>
                  Object.entries(nodes).map(([node, rank]) => (
                    <tr key={`${tree}/${node}`}>
                      <td className="text-fg-dim">{tree}</td>
                      <td className="font-medium">{node}</td>
                      <td className="font-mono tabular-nums">{rank}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Actions */}
      <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-fg-dim mb-3">Actions</h2>
      <div className="card mb-4">
        <h3 className="text-sm font-semibold m-0 mb-3">Grant XP</h3>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
          <div>
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">Track ID</label>
            <input className="input" value={trackId} onChange={e => setTrackId(e.target.value)} />
          </div>
          <div>
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">Amount</label>
            <input className="input" type="number" value={xpAmount} onChange={e => setXpAmount(parseInt(e.target.value, 10) || 0)} style={{ width: 120 }} />
          </div>
          <button className="btn btn-primary" disabled={busy} onClick={grantXp}>Grant XP</button>
        </div>
      </div>

      <div className="card">
        <h3 className="text-sm font-semibold m-0 mb-3">Unlock skill</h3>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <div>
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">Tree ID</label>
            <input className="input" value={treeId} onChange={e => setTreeId(e.target.value)} />
          </div>
          <div>
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">Node ID</label>
            <input className="input" value={nodeId} onChange={e => setNodeId(e.target.value)} />
          </div>
          <button className="btn btn-primary" disabled={busy} onClick={unlockSkill}>Unlock</button>
        </div>
        <div className="text-xs text-fg-muted mt-3">Requires skill points from level-ups. Define tracks + trees in Config Explorer first.</div>
      </div>
    </Container>
  );
}
